import { FormName } from '@core/data-access/models/form/form.model';
import { OrderResponseModel } from '@core/data-access/models/save-order/order-response.model';
import { OrderPayloadModel } from '@core/data-access/models/save-order/order-store.model';
import { cloneDeep, every, forEach, isEmpty, keys, reduce } from 'lodash';
import {
  FormInputsPayloadModel,
  FormStatusPayloadModel,
  OneStepModel,
  StepName,
  StepValidities,
  VisibilityPayloadModel
} from './step.interface';
import { ActionTypes, Actions } from './steps-forms.actions';
import { getSortedVisibleStepValidities } from './utils/step-validities';
import { mapStepRouteToStepName } from './utils/step-validity-mappers';

export interface State {
  [StepName.Tickets]: OneStepModel;
  [StepName.Workshop]: OneStepModel;
  [StepName.Menu]: OneStepModel;
  [StepName.Legitimation]: OneStepModel;
  [StepName.Confirmation]: OneStepModel;
  [StepName.Recipe]: OneStepModel;
  [StepName.Payment]: OneStepModel;
  [StepName.Invoice]: OneStepModel;
  validity: StepValidities;
  selectedStep: StepName;
  isContinueAsGuest: boolean;
  orderResponse: OrderResponseModel;
  paymentMethod: string;
  anonymousHiddenSteps: StepName[];
}

export const initialState: State = {
  [StepName.Tickets]: {
    forms: null
  },
  [StepName.Workshop]: {
    forms: null
  },
  [StepName.Menu]: {
    forms: null
  },
  [StepName.Legitimation]: {
    forms: null
  },
  [StepName.Confirmation]: {
    forms: {
      [FormName.ConfirmationCheckboxes]: {
        rerender: true,
        list: []
      }
    }
  },
  [StepName.Recipe]: {
    forms: null
  },
  [StepName.Payment]: {
    forms: null
  },
  [StepName.Invoice]: {
    forms: null
  },
  validity: {
    [StepName.Tickets]: {
      valid: true,
      disabled: false,
      forms: {
        login: false,
        ticketSelection: false
      },
      order: 2,
      visible: true,
      showInStepNavigation: true
    },
    [StepName.Legitimation]: {
      valid: false,
      disabled: true,
      order: 4,
      visible: false,
      forms: {
        [FormName.Legitimation]: false
      },
      showInStepNavigation: true
    },
    [StepName.BuyerInformation]: {
      valid: false,
      disabled: true,
      forms: {
        [FormName.BuyerInformation]: false,
        [FormName.BuyerBillingAddress]: false,
        [FormName.BuyerPrivacyPolicy]: false
      },
      order: 5,
      visible: true,
      showInStepNavigation: true
    },
    [StepName.BuyerQuestionnaire]: {
      valid: false,
      disabled: true,
      forms: {
        [FormName.BuyerQuestionnaire]: false
      },
      order: 6,
      visible: false,
      showInStepNavigation: true
    },
    [StepName.TicketAssignment]: {
      valid: false,
      disabled: true,
      forms: {
        [FormName.TicketHolder]: false,
        [FormName.TicketHolderQuestionnaire]: false
      },
      order: 7,
      visible: false,
      showInStepNavigation: true
    },
    [StepName.Workshop]: {
      valid: false,
      disabled: true,
      order: 10,
      visible: false,
      forms: {
        [FormName.Workshop]: false
      },
      showInStepNavigation: true
    },
    [StepName.Menu]: {
      valid: true,
      disabled: false,
      order: 12,
      visible: false,
      showInStepNavigation: true
    },
    [StepName.Confirmation]: {
      valid: false,
      disabled: true,
      order: 14,
      visible: true,
      forms: {
        [FormName.ConfirmationCheckboxes]: false
      },
      showInStepNavigation: true
    },
    [StepName.Recipe]: {
      valid: false,
      disabled: true,
      order: 15,
      visible: false,
      showInStepNavigation: true
    },
    [StepName.Payment]: {
      valid: false,
      disabled: true,
      order: 16,
      visible: false,
      forms: {
        [FormName.Payment]: false
      },
      showInStepNavigation: false
    },
    [StepName.Invoice]: {
      valid: false,
      disabled: true,
      order: 17,
      visible: false,
      showInStepNavigation: true
    }
  },
  selectedStep: null,
  isContinueAsGuest: false,
  orderResponse: null,
  paymentMethod: null,
  anonymousHiddenSteps: []
};

export function reducer(state = initialState, action: Actions): State {
  const clonedInitialState: State = cloneDeep(initialState);

  switch (action.type) {
    case ActionTypes.SET_SELECTED_STEP:
      return {
        ...state,
        selectedStep: mapStepRouteToStepName(action.payload) as StepName
      };

    case ActionTypes.SET_FORM_INPUTS: {
      const inputsPayload = action.payload as FormInputsPayloadModel;

      if (isEmpty(inputsPayload)) {
        return state;
      }

      const stepClone = { ...state[inputsPayload.formInfo[0]] };

      stepClone.forms[inputsPayload.formInfo[1]] = inputsPayload.inputSet;

      return {
        ...state,
        [inputsPayload.formInfo[0]]: stepClone
      };
    }
    case ActionTypes.REMOVE_FORM: {
      const formActionName = action.payload;
      const stepClone = cloneDeep(state[formActionName[0]]);
      delete stepClone.forms[formActionName[1]];

      return {
        ...state,
        [formActionName[0]]: stepClone
      };
    }
    case ActionTypes.SET_STEP_VISIBILITY: {
      const visibilityPayload = action.payload as VisibilityPayloadModel[];

      const updatedStateValidity = cloneDeep(state.validity);

      forEach(visibilityPayload, ({ stepKey, visible }) => {
        updatedStateValidity[stepKey].visible = visible;
      });

      return {
        ...state,
        validity: updatedStateValidity
      };
    }

    case ActionTypes.SET_MULTIPLE_STEPS_VISIBILITY: {
      const visibilityPayload = action.payload as VisibilityPayloadModel[];
      const stateValidityClone = cloneDeep(state.validity);
      visibilityPayload.forEach((step, index: number) => {
        stateValidityClone[step.stepKey].visible = step.visible;
      });

      return {
        ...state,
        validity: stateValidityClone
      };
    }

    case ActionTypes.SET_STEP_ORDER: {
      const orderPayload = action.payload as OrderPayloadModel;
      const stateValidityClone = cloneDeep(state.validity);
      stateValidityClone[orderPayload.stepKey].order = orderPayload.newOrder;

      return {
        ...state,
        validity: stateValidityClone
      };
    }

    case ActionTypes.SET_FORM_VALIDITY: {
      const {
        formInfo: [stepName, formName],
        valid
      } = action.payload as FormStatusPayloadModel;

      const validityClone = cloneDeep(state.validity);
      const stepsValidity = validityClone[stepName];

      stepsValidity.forms[formName] = valid;

      const areAllFormsValid = keys(stepsValidity.forms)
        .map((formName) => stepsValidity.forms[formName])
        .reduce((agr, current: boolean) => current && agr);

      stepsValidity.valid = areAllFormsValid;

      // once validity is set for all steps, we can tell which steps are disabled
      keys(validityClone)
        .sort((a, b) => validityClone[a].order - validityClone[b].order)
        .filter((stepName) => validityClone[stepName].visible)
        .reduce(
          (previousStep, currentStepName) => {
            validityClone[currentStepName].disabled = previousStep.disabled || !previousStep.valid;

            return {
              valid: validityClone[currentStepName].valid,
              disabled: validityClone[currentStepName].disabled
            };
          },
          { valid: true, disabled: false }
        );

      return {
        ...state,
        validity: validityClone
      };
    }

    case ActionTypes.SET_STEP_FORMS_VALIDITY: {
      const { stepName, formValidities } = action.payload;

      const validitiesClone = cloneDeep(state.validity);
      const stepValidity = validitiesClone[stepName];

      forEach(formValidities, (isValid, formName) => {
        stepValidity.forms[formName] = isValid;
      });

      stepValidity.valid = every(stepValidity.forms);

      const sortedVisibleStepValidities = getSortedVisibleStepValidities(validitiesClone);

      reduce(
        sortedVisibleStepValidities,
        (previousStepState, currentStep: StepName) => {
          const { valid } = validitiesClone[currentStep];

          const disabled = previousStepState.disabled || !previousStepState.valid;

          validitiesClone[currentStep].disabled = disabled;

          return {
            valid,
            disabled
          };
        },
        { valid: true, disabled: false }
      );

      return {
        ...state,
        validity: validitiesClone
      };
    }

    case ActionTypes.REMOVE_FORMS_VALIDITY: {
      const validityPayloads = action.payload as FormStatusPayloadModel[];
      if (validityPayloads.length) {
        const validityClone = cloneDeep(state.validity);

        validityPayloads.forEach((validityPayload) => {
          delete validityClone[validityPayload.formInfo[0]].forms[validityPayload.formInfo[1]];
        });

        const validityPayloadFormName = validityPayloads[0].formInfo[0];

        const wholeFormValid = Object.keys(validityClone[validityPayloadFormName].forms)
          .map((formName) => {
            return validityClone[validityPayloadFormName].forms[formName];
          })
          .reduce((agr, current: boolean) => {
            return current && agr;
          });

        validityClone[validityPayloadFormName].valid = wholeFormValid;

        // once validity is set for all steps, we can tell which steps are disabled
        Object.keys(validityClone)
          .sort((a, b) => {
            return validityClone[a].order - validityClone[b].order;
          })
          .filter((stepKey) => {
            return validityClone[stepKey].visible;
          })
          .reduce(
            (lastStep, currentKey) => {
              validityClone[currentKey].disabled = lastStep.disabled || !lastStep.valid;
              return {
                valid: validityClone[currentKey].valid,
                disabled: validityClone[currentKey].disabled
              };
            },
            { valid: true, disabled: false }
          );

        return {
          ...state,
          validity: validityClone
        };
      } else {
        return state;
      }
    }

    case ActionTypes.SET_CONTINUE_AS_GUEST: {
      const isContinueAsGuest = action.payload;
      return {
        ...state,
        isContinueAsGuest
      };
    }

    case ActionTypes.SET_ORDER_RESPONSE: {
      const orderResponse = action.payload;
      return {
        ...state,
        orderResponse
      };
    }

    case ActionTypes.SET_PAYMENT_METHOD: {
      const paymentMethod = action.payload;
      return {
        ...state,
        paymentMethod
      };
    }

    case ActionTypes.RESET_REDUCER:
      return clonedInitialState;

    case ActionTypes.SET_ANONYMOUS_HIDDEN_STEPS: {
      return {
        ...state,
        anonymousHiddenSteps: action.payload
      };
    }

    default: {
      return state;
    }
  }
}

/**
 * Because the data structure is defined within the reducer it is optimal to
 * locate our selector functions at this level. If store is to be thought of
 * as a database, and reducers the tables, selectors can be considered the
 * queries into said database. Remember to keep your selectors small and
 * focused so they can be combined and composed to fit each particular
 * use-case.
 */

export const getTicketsStep = (state: State) => state[StepName.Tickets];
export const getWorkshopStep = (state: State) => state[StepName.Workshop];
export const getMenuStep = (state: State) => state[StepName.Menu];
export const gelLegitimationStep = (state: State) => state[StepName.Legitimation];
export const getConfirmationStep = (state: State) => state[StepName.Confirmation];
export const getRecipeStep = (state: State) => state[StepName.Recipe];
export const getPaymentStep = (state: State) => state[StepName.Payment];
export const getInvoiceStep = (state: State) => state[StepName.Invoice];
export const getSelectedStep = (state: State) => state.selectedStep;
export const getSteps = (state: State) => {
  const { tickets, workshop, menu, legitimation, confirmation, recipe, payment, invoice } = state;

  return {
    tickets,
    workshop,
    menu,
    legitimation,
    confirmation,
    recipe,
    payment,
    invoice
  };
};
export const getStepsValidity = (state: State) => state.validity;
export const getConfirmationCheckboxesInputs = (state: State) =>
  state.confirmation.forms[FormName.ConfirmationCheckboxes];
export const isContinueAsGuest = (state: State) => state.isContinueAsGuest;
export const getOrderResponse = (state: State) => state.orderResponse;
export const getPaymentMethod = (state: State) => state.paymentMethod;
export const getAnonymousHiddenSteps = (state: State) => state.anonymousHiddenSteps;
